import { Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import moment from 'moment';
import { Observable, of, forkJoin, Subscription, filter, switchMap, map, tap, catchError, take } from 'rxjs';

import { Schedule } from '@shared/schedule/models';
import { Service } from '@shared/service/models';
import { Resource } from '@shared/resource/models';
import { Location } from '@shared/location/models';
import { PartyTypes } from '@shared/party/enums';
import {
  IParty,
  ScheduleService,
  ConfigurePartyService,
  IDataNewPartyApiResponse,
} from '@tablesready/common-ui';
import { DateService, PartiesService } from '../../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { v4 } from 'uuid';

@Component({
  selector: 'tablesready-edit-resources',
  templateUrl: './edit-resources.component.html',
  styleUrls: ['./edit-resources.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditResourcesComponent implements OnInit, OnDestroy {
  @Input() party: IParty;
  @Input() location: Location;
  @Input() schedule: Schedule[];
  @Input() title: string;
  icon = AppIcon;
  msPerMin = 60000;
  private timezone: string;
  private readonly subscription = new Subscription();

  servicesWarning: boolean;
  formErrors: { [param in 'isOutOfScheduleError' | 'overlapError' | 'resourcesAvailabilityError' | 'resourcesSizeError'] };
  resourcesForm: UntypedFormGroup;
  services: Service[];
  resources: Resource[];
  availablePartyDuration = 0;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly modalController: FrontendModalService,
    private readonly partiesService: PartiesService,
    private readonly alertsService: AlertsService,
    private readonly configureParty: ConfigurePartyService,
    private readonly scheduleService: ScheduleService,
    private readonly dateService: DateService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  get servicesControl() {
    return this.resourcesForm.get('assignList.services');
  }

  get resourcesControl() {
    return this.resourcesForm.get('assignList.resources');
  }

  get overrideByResourcesControl() {
    return this.resourcesForm.get('overrideResources.overrideByResources');
  }

  get overrideBySizeControl() {
    return this.resourcesForm.get('overrideResources.overrideBySize');
  }

  get overrideByWorkHoursControl() {
    return this.resourcesForm.get('overrideResources.overrideByWorkHours');
  }

  getNewPartyData(selectedServices?: string[] | boolean, selectedResources?: string[] | boolean) {
    const services = selectedServices || this.servicesControl.value || [];
    const resources = selectedResources || this.resourcesControl.value || [];
    const overrideNoResources = this.overrideByResourcesControl.value;

    return {
      ...this.party,
      resources,
      services,
      overrideNoResources,
    };
  }

  ngOnInit() {
    this.services = [];
    this.resources = [];
    this.timezone = this.location.timezone;
    this.initForm();

    this.subscription.add(this.getAvailableResourcesServices(this.party)
      .subscribe(() => {
        // First time we get form data
        this.setForm(this.party.services, this.party.resources);
        this.watchControls();
        this.tryAutoselect();
        this.checkServices(this.servicesControl.value || []);
        this.cdr.detectChanges();
      }));
  }

  initForm(): void {
    // Only show all resources when both services and online reservations are disabled
    const overrideNoResources = !((this.location?.serviceSettings?.enabled || false) || (this.location?.bookingOptions?.onlineReservationsEnabled || false));

    this.resourcesForm = this.fb.group({
      assignList: this.fb.group({
        services: [, [Validators.required]],
        resources: [, [Validators.required]],
      }),
      overrideResources: this.fb.group({
        overrideBySize: [false],
        overrideByResources: [overrideNoResources || false],
        overrideByWorkHours: [false],
      }),
    });
  }

  setForm(selectedServices?: string[], selectedResources?: string[]): void {
    this.resourcesForm.patchValue({
      assignList: {
        services: selectedServices || [],
        resources: selectedResources || [],
      },
    });
  }

  cancel(): void {
    this.modalController.dismiss();
  }

  tryAutoselect(): void {
    const party = this.getNewPartyData();
    // If there's only 1 service, assign it (if nothing already assigned)
    if (this.services?.length === 1 && !(party.services || []).length) {
      this.setForm([this.services[0]._id]);
    }
  }

  saveAndMarkSeated(): void {
    const party = this.getNewPartyData();

    this.partiesService.saveParty(party)
      .pipe(
        take(1),
        tap((data) => {
          this.modalController.dismiss(data);
          this.alertsService.add({

            type: IAlertTypes.success,
            heading: 'Success:',
            body: 'updated successfully',
          });
        }),
        catchError((err) => {
          const label = this.location.displayOptions?.labels?.customer;
          this.alertsService.addError(`new ${label || 'customer'} not added successfully: ${err}`);

          return of(err);
        }),
      )
      .subscribe();
  }

  getAvailableResourcesServices(party: IParty): Observable<IDataNewPartyApiResponse> {
    const dateMoment = moment(this.party.quoted_time).tz(this.timezone);
    const dateMillis = dateMoment.valueOf();
    const dateStr = dateMoment.format('x');

    const {
      services,
      resources,
      size,
      duration,
      _id,
    } = party;
    if (!dateStr && !services && !resources) {
      throw Error('Insufficient data');
    }
    const overrideNoResources = this.overrideByResourcesControl.value;
    const type: PartyTypes = party.type === PartyTypes.reservation ? party.type : PartyTypes.walkIn;
    const hoursWithDate = [[dateMillis, dateMillis + duration]] as [number, number][];

    return this.partiesService.findResourcesServicesForNewParty(dateStr, type, services, resources, size, duration, _id, overrideNoResources, hoursWithDate)
      .pipe(
        map((data) => {
          this.resources = (data as any).availableResources;
          this.services = (data as any).availableServices;

          return data;
        }),
        tap(() => {
          this.checkServicesAvailability(this.services, this.location);
        }),
        catchError((error) => {
          this.alertsService.add({

            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `resources and services not found: ${error}`,
          });

          throw Error('resources and services not found');
        }),
      );
  }

  checkServicesAvailability(services: Service[], location: Location): void {
    this.servicesWarning = !services.length && location?.serviceSettings?.enabled;
  }

  watchControls(): void {
    this.subscription.add(this.resourcesControl.valueChanges.pipe(switchMap((selectedResources: string[]) => {
      const party = this.getNewPartyData(false, selectedResources);

      return forkJoin([of(selectedResources), this.getAvailableResourcesServices(party)]);
    }))
      .subscribe(([selectedResources, res]) => {
        if (selectedResources?.length) {
          const resourcesCapacity = this.configureParty.calculateCapacityInSelectedResources(this.resources, selectedResources);
          const resourcesSizeError = this.checkResourcesCapacity(resourcesCapacity);
          this.setErrorsToForm(resourcesSizeError);
        }
        const overlapError = this.checkIsOverlapped(res);
        this.setErrorsToForm(overlapError);
        this.cdr.detectChanges();
      }));

    this.subscription.add(this.servicesControl.valueChanges
      .pipe(switchMap((selectedServices: string[]) => {
        this.party.duration = this.dateService.checkServicesDuration(this.services, selectedServices);
        const party = this.getNewPartyData(selectedServices, false);

        return forkJoin([of(selectedServices), this.getAvailableResourcesServices(party)]);
      }))
      .subscribe(([selectedServices]) => {
        this.checkServices(selectedServices);
        this.cdr.detectChanges();
      }));

    this.subscription.add(this.overrideBySizeControl.valueChanges
      .pipe(filter((value => Boolean(value)))).subscribe(() => {
        this.setErrorsToForm({ resourcesSizeError: null });
        this.cdr.detectChanges();
      }));

    this.subscription.add(this.overrideByResourcesControl.valueChanges
      .pipe(
        filter((value => Boolean(value))),
        switchMap((isOverride) => {
          const party = this.getNewPartyData();

          return forkJoin([of(isOverride), this.getAvailableResourcesServices(party)]);
        }),
      )
      .subscribe(() => {
        this.setErrorsToForm({ resourcesAvailabilityError: null });
        this.cdr.detectChanges();
      }));

    this.subscription.add(this.overrideByWorkHoursControl.valueChanges
      .pipe(filter((value => Boolean(value))))
      .subscribe(() => {
        this.setErrorsToForm({ isOutOfScheduleError: null });
        this.cdr.detectChanges();
      }));
  }

  getAvailableDuration(overlapedQuotedTime: number): number {
    return overlapedQuotedTime - moment.tz(this.party.quoted_time, this.timezone).valueOf();
  }

  setAvailableDuration(): void {
    if (this.availablePartyDuration) {
      this.party.duration = this.availablePartyDuration;
      this.setErrorsToForm({ overlapError: null });
    }
    this.availablePartyDuration = 0;
  }

  // Validation methods

  checkResourcesCapacity(resourcesCapacity: { min: number; max: number }): { resourcesSizeError: { deltaResourcesSize: number; determiner: string; determinerChange: string } | null } {
    const isMoreCapacity = this.party?.size > resourcesCapacity.max || false;
    const isLessCapacity = this.party?.size < resourcesCapacity.min || false;

    if (this.location?.serviceSettings?.enabled &&
      this.location?.bookingOptions?.onlineReservationsEnabled &&
      (isLessCapacity || isMoreCapacity)) {
      let sizeAlertConfig = null;

      if (isLessCapacity) {
        sizeAlertConfig = {
          deltaResourcesSize: resourcesCapacity.min - this.party.size,
          determiner: 'less',
          determinerChange: 'increase',
        };
      }
      if (isMoreCapacity) {
        sizeAlertConfig = {
          deltaResourcesSize: this.party.size - resourcesCapacity.max,
          determiner: 'more',
          determinerChange: 'decrease',
        };
      }

      return { resourcesSizeError: { ...sizeAlertConfig } };
    }

    return { resourcesSizeError: null };
  }

  checkIsWorkTime(selectedServices: string[]): boolean {
    const partyDuration = this.dateService.checkServicesDuration(this.services, selectedServices);
    const partyTime = Number(moment(this.party.quoted_time).tz(this.timezone));
    const { hours, exceptions } = this.schedule[0];

    return this.scheduleService.check(hours, exceptions, partyTime, this.timezone, partyDuration);
  }

  checkResourcesAvailability(selectedServices: string[]): { resourcesAvailabilityError: boolean | null } {
    const isWorkTime = this.checkIsWorkTime(selectedServices);
    const isNoResources = !this.resources?.length;
    const isChoosenServices = Boolean(selectedServices.length);

    return { resourcesAvailabilityError: (isWorkTime && isNoResources && isChoosenServices) || null };
  }

  checkIsOutOfSchedule(selectedServices: string[]): { isOutOfScheduleError: boolean | null } {
    const isWorkTime = this.checkIsWorkTime(selectedServices);

    return { isOutOfScheduleError: (!isWorkTime && (selectedServices?.length !== 0)) || null };
  }

  checkIsOverlapped(data: IDataNewPartyApiResponse): { overlapError: { availablePartyDurationInMin: number } | null } {
    const isOverlapped = (data?.overlappingParty || []).length && this.location?.bookingOptions?.onlineReservationsEnabled;
    if (isOverlapped && !this.location?.bookingOptions?.allowMultipleParties) {
      this.availablePartyDuration = this.getAvailableDuration(data.overlappingParty[0]?.quoted_time);
      const availablePartyDurationInMin = Math.round(this.availablePartyDuration / this.msPerMin);

      return { overlapError: { availablePartyDurationInMin } };
    }

    return { overlapError: null };
  }

  checkServices(selectedServices: string[]): void {
    const resourcesAvailabilityError = this.checkResourcesAvailability(selectedServices);
    const isOutOfScheduleError = this.checkIsOutOfSchedule(selectedServices);
    this.setErrorsToForm({
      ...resourcesAvailabilityError,
      ...isOutOfScheduleError,
    });
  }

  checkInvalid(): boolean {
    return this.formErrors?.resourcesSizeError ||
      this.formErrors?.resourcesAvailabilityError ||
      this.formErrors?.isOutOfScheduleError ||
      this.formErrors?.overlapError ||
      this.resourcesForm.invalid;
  }

  setErrorsToForm(error): void {
    this.formErrors = {
      ...this.formErrors,
      ...error,
    };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getResourceTextWithSize(resource: Resource): string {
    return `${resource.name} (${resource.capacity.max})`;
  }

  getResourceText(resource: Resource): string {
    return resource.name;
  }
}
